import * as chrono from 'chrono-node'
import i18next from 'i18next'
import type { StartPeriod } from '@/src/renderer/lib/db/models'
import type { StartValue } from '../components/WhenDropdown/WhenDropdown.types'

export type { StartValue }

/** Default time ranges for each period */
export const PERIOD_RANGES: Record<StartPeriod, { start: string; end: string }> = {
  morning: { start: '08:00', end: '12:00' },
  noon: { start: '12:00', end: '13:00' },
  afternoon: { start: '13:00', end: '17:00' },
  evening: { start: '17:00', end: '20:00' },
}

/** Period keywords mapped to StartPeriod (German + English) */
const PERIOD_KEYWORDS: Record<string, StartPeriod> = {
  // German
  vormittag: 'morning',
  vormittags: 'morning',
  mittag: 'noon',
  mittags: 'noon',
  nachmittag: 'afternoon',
  nachmittags: 'afternoon',
  abend: 'evening',
  abends: 'evening',
  // English
  morning: 'morning',
  noon: 'noon',
  afternoon: 'afternoon',
  evening: 'evening',
}

// Compile each keyword's word-boundary regex once at module scope —
// extractPeriod runs in a hot path on every task-input keystroke.
const PERIOD_KEYWORD_REGEXES: Array<{ regex: RegExp; period: StartPeriod }> =
  Object.entries(PERIOD_KEYWORDS).map(([keyword, period]) => ({
    regex: new RegExp(`\\b${keyword}\\b`, 'i'),
    period,
  }))

/**
 * Extract a period keyword from the input string.
 * Returns the matched period and the input with the keyword removed.
 */
function extractPeriod(input: string): { period: StartPeriod | null; cleaned: string } {
  const lower = input.toLowerCase()
  for (const { regex, period } of PERIOD_KEYWORD_REGEXES) {
    if (regex.test(lower)) {
      return { period, cleaned: input.replace(regex, '').trim() }
    }
  }
  return { period: null, cleaned: input }
}

/**
 * Parse a natural language date/time string into a structured StartValue.
 * Supports both German and English via chrono-node.
 *
 * @param input - Natural language string like "morgen um 14 Uhr", "today afternoon"
 * @param locale - 'de' or 'en'
 * @param referenceDate - Reference date for relative parsing (defaults to now)
 */
export function parseStartDate(
  input: string,
  locale: string = 'de',
  referenceDate?: Date,
): StartValue | null {
  if (!input.trim()) return null

  // 1. Check for period keywords first
  const { period, cleaned } = extractPeriod(input)

  // 2. Parse with chrono-node
  const parser = locale.startsWith('de') ? chrono.de : chrono.en
  const ref = referenceDate ?? new Date()
  const results = parser.parse(cleaned || input, ref, { forwardDate: true })

  if (results.length === 0 && !period) return null

  const result = results[0]

  // If chrono found a date
  if (result) {
    const parsed = result.start
    const date = parsed.date()

    // Check if chrono found a specific time
    const hasTime = parsed.isCertain('hour')

    if (hasTime && !period) {
      const hours = String(parsed.get('hour') ?? 0).padStart(2, '0')
      const minutes = String(parsed.get('minute') ?? 0).padStart(2, '0')
      return { date, time: `${hours}:${minutes}`, period: null }
    }

    return { date, time: null, period: period ?? null }
  }

  // Period without a parseable date → use reference date (today)
  if (period) {
    return { date: ref, time: null, period }
  }

  return null
}

/**
 * Format a StartValue as a natural language display string.
 *
 * Examples:
 * - "Heute" / "Today"
 * - "Morgen Nachmittag" / "Tomorrow Afternoon"
 * - "Fr. 18. Apr" / "Fri, Apr 18"
 * - "Fr. 18. Apr, 14:15 Uhr" / "Fri, Apr 18, 2:15 PM"
 */
export function formatStartDate(
  value: StartValue,
  locale: string = 'de',
  referenceDate?: Date,
): string {
  const ref = referenceDate ?? new Date()
  const isDE = locale.startsWith('de')

  const datePart = formatDatePart(value.date, ref, isDE, true)

  if (value.period) {
    const periodLabel = getPeriodLabel(value.period, isDE)
    return `${datePart} ${periodLabel}`
  }

  if (value.time) {
    const isTodayOrTomorrow = isDateTodayOrTomorrow(value.date, ref)
    const separator = isTodayOrTomorrow ? ' ' : ', '
    if (isDE) {
      return `${datePart}${separator}${value.time}`
    }
    return `${datePart}${separator}${formatTime12h(value.time)}`
  }

  return datePart
}

/** Compute the day-difference between a target date and a reference date (ignoring time). */
export function dateDiffDays(date: Date, ref: Date): number {
  const today = new Date(ref)
  today.setHours(0, 0, 0, 0)
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

/** Resolve an i18n key with hardcoded fallback (for use outside React and in tests). */
function t(key: string, fallback: string): string {
  if (!i18next.isInitialized) return fallback
  const val = i18next.t(key)
  return val === key ? fallback : val
}

/** Format a date as a short human-readable string (e.g. "Heute", "Morgen", "Freitag", "Fr 18. Apr"). */
export function formatDatePart(date: Date, ref: Date, isDE: boolean, hideCurrentYear = false): string {
  const diffDays = dateDiffDays(date, ref)
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)

  if (diffDays === 0) return t('taskCreation.whenToday', isDE ? 'Heute' : 'Today')
  if (diffDays === 1) return t('taskCreation.whenTomorrow', isDE ? 'Morgen' : 'Tomorrow')

  if (diffDays >= 2 && diffDays <= 6) {
    return target.toLocaleDateString(isDE ? 'de-DE' : 'en-US', { weekday: 'long' })
  }

  const showYear = !(hideCurrentYear && target.getFullYear() === ref.getFullYear())

  if (isDE) {
    const weekday = target.toLocaleDateString('de-DE', { weekday: 'short' })
    const day = target.getDate()
    const month = target.toLocaleDateString('de-DE', { month: 'short' })
    return showYear
      ? `${weekday} ${day}. ${month} ${target.getFullYear()}`
      : `${weekday} ${day}. ${month}`
  }

  return target.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(showYear ? { year: 'numeric' } : {}),
  })
}

/** Check if a date is today or tomorrow relative to a reference date. */
export function isDateTodayOrTomorrow(date: Date, ref: Date): boolean {
  const diffDays = dateDiffDays(date, ref)
  return diffDays === 0 || diffDays === 1
}

const PERIOD_FALLBACKS: Record<StartPeriod, { de: string; en: string }> = {
  morning: { de: 'Vormittag', en: 'Morning' },
  noon: { de: 'Mittag', en: 'Noon' },
  afternoon: { de: 'Nachmittag', en: 'Afternoon' },
  evening: { de: 'Abend', en: 'Evening' },
}

/** Get the localized label for a period (e.g. "Vormittag", "Morning"). */
export function getPeriodLabel(period: StartPeriod, isDE?: boolean): string {
  const de = isDE ?? i18next.language?.startsWith('de') ?? true
  return t(`taskCreation.whenPeriod_${period}`, de ? PERIOD_FALLBACKS[period].de : PERIOD_FALLBACKS[period].en)
}

/** Format a 24h time string to 12h format (e.g. "14:30" → "2:30 PM"). */
export function formatTime12h(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const hour12 = h % 12 || 12
  return m > 0 ? `${hour12}:${String(m).padStart(2, '0')} ${period}` : `${hour12} ${period}`
}

/**
 * Format a StartValue for the badge display (short form).
 * E.g. "Fr. 9. Jan" or "Heute"
 */
export function formatStartBadge(
  value: StartValue,
  locale: string = 'de',
  referenceDate?: Date,
): string {
  return formatDatePart(value.date, referenceDate ?? new Date(), locale.startsWith('de'), true)
}

/**
 * Format a start date for the collapsed task row badge.
 *
 * - diff 2–6 days: abbreviated weekday without trailing dot ("Mo", "Fr" / "Thu", "Fri")
 * - diff 7+ days: day + short month ("30. Apr" / "Apr 30")
 * - diff 0–1 (today/tomorrow): NOT handled here — use TodayIcon/TomorrowIcon instead
 */
export function formatStartCollapsedBadge(
  date: Date,
  locale: string = 'de',
  referenceDate?: Date,
): string {
  const ref = referenceDate ?? new Date()
  const diff = dateDiffDays(date, ref)
  const isDE = locale.startsWith('de')
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)

  if (diff >= 2 && diff <= 6) {
    const raw = target.toLocaleDateString(isDE ? 'de-DE' : 'en-US', { weekday: 'short' })
    return raw.replace(/\.$/, '')
  }

  if (isDE) {
    const day = target.getDate()
    const month = target.toLocaleDateString('de-DE', { month: 'short' }).replace(/\.$/, '')
    return `${day}. ${month}`
  }
  return target.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
